/**
 * Admin Analytics Types
 * Mirrors backend types for type safety
 */

export interface PlanDistribution {
  slug: string;
  name: string;
  display_name: string;
  count: number;
  percentage: number;
}

export interface UsageTrend {
  date: string;
  feature_key: string;
  total_usage: number;
}

export interface ConversionFunnelData {
  from_plan: string;
  to_plan: string;
  conversion_rate: number;
  total_upgrades: number;
  description?: string;
}

export interface TopFeature {
  feature_key: string;
  usage_count: number;
  percentage: number;
}

export interface LimitExceededData {
  count: number;
  near_limit_count: number;
  trend_direction: 'up' | 'down';
  trend_percentage: number;
  by_feature: Array<{
    feature_key: string;
    users_exceeded: number;
    users_near_limit: number;
    total_at_risk: number;
    last_activity: string;
  }>;
  recent_activity: Array<{
    feature_key: string;
    event_count: number;
    unique_users: number;
  }>;
}

export interface MRRTrendData {
  month: string;
  mrr: number;
  paid_invoices: number;
  paying_customers: number;
  active_subscriptions: number;
}

export interface SubscriptionGrowthData {
  date: string;
  new_subscriptions: number;
  cumulative_total: number;
}

export interface UserGrowthMetrics {
  total_users: number;
  verified_users: number;
  new_users_30d: number;
  verification_rate: number;
}

/**
 * Prompt statistics from the prompts table (REAL DATA)
 */
export interface PromptStats {
  total_prompts: number;
  public_prompts: number;
  private_prompts: number;
  total_views: number;
  total_votes: number;
  unique_authors: number;
  by_category: Array<{
    category_name: string;
    prompt_count: number;
  }>;
  by_model: Array<{
    model_type: string;
    prompt_count: number;
  }>;
  daily_activity: Array<{
    date: string;
    prompts_created: number;
    public_created: number;
    private_created: number;
  }>;
}

/**
 * Content analytics from actual data tables (prompts, collections, etc.)
 * This gives REAL counts instead of usage_counters which may be outdated
 */
export interface ContentAnalytics {
  feature_key: string;
  usage_count: number;
  percentage: number;
}

export type DateRange = {
  start: string; // '-30d', '-7d', '-90d', or ISO date
  end: string;   // 'now' or ISO date
};

export const DATE_RANGE_PRESETS = {
  'Last 7 Days': { start: '-7d', end: 'now' },
  'Last 30 Days': { start: '-30d', end: 'now' },
  'Last 90 Days': { start: '-90d', end: 'now' },
  'This Year': { start: '-365d', end: 'now' },
} as const;

export type DateRangePreset = keyof typeof DATE_RANGE_PRESETS;
